import React, {createContext, useContext, useState, ReactNode, useRef, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {
    Box,
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useMultiStyleConfig,
    FlexProps,
    TextProps,
    StackProps,
    VStack,
    BoxProps,
    VisuallyHidden,
} from '@chakra-ui/react'
import {IconButton, Icon, Picture} from '_uiComponents'
import type {IconNameTypes} from '_types'

// Sizes allowed for the header close (X) / back (<) icon. 'sm-plus' (18px) matches the Figma spec
// for the PDP modals/drawers (node 5655-4320).
type ClosingIconSize = 'sm' | 'sm-plus' | 'md' | 'lg'

interface ModalOptions {
    imageUrl?: string
    imageAlt?: string
    title?: string | ReactNode
    body: ReactNode
    footer?: ReactNode
    variant?: string
    closingIcon?: IconNameTypes
    closingIconSize?: ClosingIconSize
    showCloseButton?: boolean
    onClose?: () => void
    className?: string
}

interface ModalContextType {
    openModal: (options: ModalOptions) => void
    closeModal: () => void
    updateModalTitle: (title: string | ReactNode) => void
    updateModalBody: (body: ReactNode) => void
    updateModalFooter: (footer: ReactNode) => void
    updateModalImage: (url?: string, alt?: string) => void
    updateOnCloseCallback: (onCloseCallback: {fn: () => void}) => void
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

export const ModalProvider = ({children}: {children: ReactNode}) => {
    const {formatMessage} = useIntl()
    const location = useLocation()
    const [variant, setVariant] = useState<string>()
    const [isOpen, setIsOpen] = useState(false)
    const [modalImage, setModalImage] = useState<{url?: string; alt?: string}>()
    const [modalTitle, setModalTitle] = useState<string | ReactNode | undefined>()
    const [modalBody, setModalBody] = useState<ReactNode | null>(null)
    const [modalFooter, setModalFooter] = useState<ReactNode | null>(null)
    const [closingIcon, setClosingIcon] = useState<IconNameTypes>('IconClose')
    const [closingIconSize, setClosingIconSize] = useState<ClosingIconSize>('md')
    const [showCloseButton, setShowCloseButton] = useState(true)
    const [onCloseCallback, setOnCloseCallback] = useState<{fn: () => void} | null>(null)
    const [className, setClassName] = useState<string>('')

    const styles = useMultiStyleConfig('Modal', {variant}) as Record<string, FlexProps & TextProps & StackProps>

    const modalContentRef = useRef<HTMLDivElement>(null)
    const isTitleVisuallyHidden = variant === 'changeLocale' || variant === 'switchLocale'
    const modalTitleId = 'modal-title'

    const openModal = ({
        imageUrl,
        imageAlt,
        title,
        body,
        footer,
        variant,
        closingIcon = 'IconClose',
        closingIconSize = 'md',
        showCloseButton = true,
        onClose,
        className,
    }: ModalOptions) => {
        setModalImage({url: imageUrl, alt: imageAlt})
        setModalTitle(title)
        setModalBody(body)
        setModalFooter(footer)
        setOnCloseCallback({fn: () => onClose?.()})
        setVariant(variant)
        setIsOpen(true)
        setClosingIcon(closingIcon)
        setClosingIconSize(closingIconSize)
        setClassName(className || '')
        setShowCloseButton(showCloseButton)
    }

    const closeModal = () => {
        onCloseCallback?.fn?.()
        setIsOpen(false)
    }

    /**
     * ModalProvider is mounted once in the app shell (see _app/index.tsx), ABOVE the router, so it never
     * unmounts on client-side navigation - only the routed page below it swaps out. A Chakra `Link` (or
     * any other react-router navigation) rendered inside the modal body therefore does NOT close the
     * modal: nothing calls closeModal(), so the modal (and any cleanup its onClose performs, e.g.
     * restoring `document.body.style.overflow`) keeps floating on top of the newly navigated page.
     *
     * We only react to a PATHNAME change (a real navigation to a different page). Some in-page flows
     * (e.g. PDP color/size selection) update just the query string via `history.replace` while
     * intentionally keeping a modal open (the zoom modal swaps its media set), so those must not
     * trigger a close here.
     */
    const pathnameRef = useRef(location.pathname)
    useEffect(() => {
        if (isOpen && location.pathname !== pathnameRef.current) {
            closeModal()
        }
        pathnameRef.current = location.pathname
    }, [location.pathname])

    const updateModalTitle = (title: string | ReactNode) => {
        setModalTitle(title)
    }

    const updateModalBody = (body: ReactNode) => {
        setModalBody(body)
    }

    const updateModalFooter = (footer: ReactNode) => {
        setModalFooter(footer)
    }

    const updateModalImage = (url?: string, alt?: string) => {
        if (modalImage?.url !== url || modalImage?.alt !== alt) {
            setModalImage({url, alt})
        }
    }

    const updateOnCloseCallback = (onCloseCallback: {fn: () => void}) => {
        setOnCloseCallback(onCloseCallback)
    }

    /**
     * Since Chakra does not support setup of `aria-labelledby` parameter on modal content
     * this setup on opening ensures that it is configured correctly pointing to the text that
     * actually contains the title label of the Modal.
     */
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                if (modalContentRef.current) {
                    modalContentRef.current.setAttribute('aria-labelledby', modalTitleId)
                }
            }, 500)
        }
    }, [isOpen])

    return (
        <ModalContext.Provider value={{openModal, closeModal, updateModalTitle, updateModalBody, updateModalFooter, updateModalImage, updateOnCloseCallback}}>
            {children}
            {/* The zoom (mediaZoom) modal renders the desktop PDP stickybar and its size/color popovers
                OUTSIDE the ModalContent subtree (they are elevated above the modal via CSS). Chakra's
                default blockScrollOnMount mounts react-remove-scroll, which blocks wheel scrolling
                everywhere except inside ModalContent — so those popovers become non-scrollable while the
                zoom is open. The zoom flow already locks the background itself (document.body.style.overflow
                = 'hidden' in handleMediaSlideClick), so disabling blockScrollOnMount for this variant only
                restores popover scrolling without letting the page scroll behind the zoom. Other modals keep
                Chakra's default scroll-lock. */}
            {/* trapFocus is also disabled for the zoom variant: when an add-to-cart from the zoom opens the
                minicart Drawer ON TOP of the still-open zoom modal, an open Chakra Modal keeps ownership of
                the focus-lock + react-remove-scroll sidecar, which suppresses the Drawer's own scroll
                whitelist and leaves the minicart content unscrollable. Releasing the zoom modal's focus
                trap lets the Drawer above manage scroll/focus normally. Safe here because the zoom overlay
                is a media viewer (no form fields to trap) and the elevated stickybar/popovers live outside
                ModalContent anyway. Other modals keep Chakra's default focus trap. */}
            <Modal isOpen={isOpen} onClose={closeModal} variant={variant} blockScrollOnMount={variant !== 'mediaZoom'} trapFocus={variant !== 'mediaZoom'}>
                <ModalOverlay />
                <ModalContent ref={modalContentRef} className={className}>
                    {(modalImage?.url || variant === 'NewsletterModal') && (
                        <Box {...(styles.imageContainer as BoxProps)}>
                            <Picture objectFit="cover" imagexs={modalImage?.url} alt={modalImage?.alt} />
                        </Box>
                    )}

                    <VStack className={'hello1'} {...styles.dialogWrapper}>
                        <VStack className={'hello2'} {...styles.bodyWrapper}>
                            <ModalHeader>
                                {modalTitle && (
                                    // A string title renders as a semantic <p> (Chakra Text). A ReactNode title
                                    // (e.g. the zoom modal header, which contains a VStack/<div>) must NOT be
                                    // wrapped in a <p>, otherwise React warns about invalid DOM nesting
                                    // (<div> cannot be a descendant of <p>). In that case use a <div> (Box).
                                    <Text
                                        id={modalTitleId}
                                        {...styles.headerTitle}
                                        as={isTitleVisuallyHidden ? VisuallyHidden : typeof modalTitle === 'string' ? Text : Box}
                                    >
                                        {modalTitle}
                                    </Text>
                                )}
                                {showCloseButton && (
                                    <Box {...styles.closeIcon}>
                                        <IconButton
                                            variant={'iconButtonBgTransparent'}
                                            size="iconButtonSm"
                                            icon={<Icon name={closingIcon} size={closingIconSize} />}
                                            onClick={closeModal}
                                            aria-label={formatMessage({id: 'alt.link.close', defaultMessage: 'Close'})}
                                        />
                                    </Box>
                                )}
                            </ModalHeader>
                            <ModalBody>{modalBody}</ModalBody>
                        </VStack>

                        {modalFooter && <ModalFooter>{modalFooter}</ModalFooter>}
                    </VStack>
                </ModalContent>
            </Modal>
        </ModalContext.Provider>
    )
}

export const useModal = (): ModalContextType => {
    const context = useContext(ModalContext)
    if (!context) {
        throw new Error('useModal must be used within a ModalProvider')
    }
    return context
}
